/**
 * Client HTTP du contrat `/v1/console` et de `/v1/auth`.
 *
 * Session cookie + en-tête `X-Artosera-CSRF` sur toute mutation (docs/dev/api.md,
 * §3). En mode `?demo`, aucune requête réseau : les fonctions répondent depuis
 * `demoData.ts`, pour la vérification visuelle sans API (S2-g).
 */
import { signalerConfinement } from './confinement.js';
import { demoApi, compteDemoCourant } from './demoData.js';
import { demoComptabilite } from './demoComptabilite.js';
import { demoSaisies } from './demoSaisies.js';
import { demoOuvertures } from './demoOuvertures.js';
import { demoDemandesSite } from './demoDemandesSite.js';
import { demoDiagnosticsIos } from './demoDiagnosticsIos.js';
import { demoVersionsApps } from './demoVersionsApps.js';
import type {
  DemandeOuvertureResume,
  DemandeOuvertureVue,
  EtatDemandeOuverture,
  ExigencesPlateforme,
  PlateformeApp,
  SourceDemandeOuverture,
} from '@artosera/shared';
import { demoDemandesOuverture } from './demoDemandesOuverture.js';
import type {
  VersionsAppsVue,
  DemandeSiteConsole,
  EtatDemandeSite,
  FiltreDemandesSite,
  FiltreDiagnosticsIos,
  DefautIos,
  DiagnosticIos,
  DisponibiliteSlug,
  DossierOuverture,
  EtatOuverture,
  ModificationOuverture,
  PageOuvertures,
  ReponseCreationGalerieOuverture,
  ControleRecette,
  RecetteOuverture,
  ReponseBascule,
  ReponseTransitionOuverture,
  SectionsOuverture,
  EtatSsoConsole,
  FiltresTravaux,
  AccesSupport,
  RoleAccesSupport,
  ApparenceGalerie,
  BeneficiaireSaisie,
  Comptabilite,
  CompteAssocie,
  CorpsSaisie,
  Domaine,
  Encaissement,
  EncaissementsDuMois,
  GroupeSaisie,
  EtatGalerieModifiable,
  GalerieFiche,
  GalerieListe,
  IdPalierDev,
  Interrupteurs,
  InvitationAssocie,
  JournalEntree,
  Me,
  MoisComptable,
  NomVueDAigle,
  NouvelleGalerie,
  PageTravaux,
  Palier,
  PaliersVue,
  PartsGrille,
  PeriodeVueDAigle,
  ReponseModules,
  ReponseConnecteursConsole,
  RenvoiExport,
  RessourceSaisie,
  Sante,
  SaisiesParRessource,
  Travail,
  TypeRecetteId,
  TypeRecetteVue,
  VersementSaisie,
  VersementsListe,
  VersionGrille,
  VueDAigle,
} from '../types.js';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const CLE_DEMO_SESSION = 'artosera-console-demo';

/**
 * Vrai si `?demo` est dans l'URL, ou l'a été plus tôt dans cet onglet : le
 * mode démonstration reste actif en naviguant dans l'app (les liens internes
 * ne portent pas la requête), le temps de la vérification visuelle (S2-g).
 */
export function modeDemo(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).has('demo')) {
    try {
      window.sessionStorage.setItem(CLE_DEMO_SESSION, '1');
    } catch {
      /* stockage indisponible (navigation privée) : le mode reste actif pour cette page */
    }
    return true;
  }
  try {
    return window.sessionStorage.getItem(CLE_DEMO_SESSION) === '1';
  } catch {
    return false;
  }
}

export async function requete<T>(
  chemin: string,
  options: { methode?: string; corps?: unknown } = {},
): Promise<T> {
  const { methode = 'GET', corps } = options;
  const entetes: Record<string, string> = {};
  if (corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (methode !== 'GET') entetes['X-Artosera-CSRF'] = '1';

  const reponse = await fetch(chemin, {
    method: methode,
    credentials: 'include',
    headers: entetes,
    body: corps === undefined ? undefined : JSON.stringify(corps),
  });

  if (reponse.status === 204) return undefined as T;

  const type = reponse.headers.get('content-type') ?? '';
  const donnees = type.includes('application/json') ? await reponse.json() : null;

  if (!reponse.ok) {
    const erreur = (donnees as { code?: string; message?: string; details?: unknown } | null) ?? {};
    const refus = new ApiError(
      reponse.status,
      erreur.code ?? 'erreur_inconnue',
      erreur.message ?? 'Une erreur est survenue.',
      erreur.details ?? null,
    );
    // Toute route peut répondre « confiné à l'enrôlement » : la grâce s'achève
    // à un instant quelconque. La console se replie alors sur `/securite`.
    if (estErreurConfinement(refus)) signalerConfinement();
    throw refus;
  }

  return donnees as T;
}

// ── Authentification ─────────────────────────────────────────────────────

export interface ReponseConnexion {
  deuxiemeFacteurRequis?: true;
  enrolementMfaRequis?: true;
  enrolementMfaSeulement?: true;
  graceMfaJusquau?: string;
}

export async function connexion(payload: {
  email: string;
  motDePasse: string;
  codeTotp?: string;
  codeSecours?: string;
}): Promise<ReponseConnexion> {
  if (modeDemo()) return {};
  return requete<ReponseConnexion>('/v1/auth/login', { methode: 'POST', corps: payload });
}

export async function deconnexion(): Promise<void> {
  if (modeDemo()) return;
  await requete<void>('/v1/auth/logout', { methode: 'POST' });
}

export async function moi(): Promise<Me> {
  if (modeDemo()) return compteDemoCourant();
  return requete<Me>('/v1/auth/me');
}

// ── Galeries ──────────────────────────────────────────────────────────────

export async function listerGaleries(): Promise<GalerieListe[]> {
  if (modeDemo()) return demoApi.listerGaleries();
  const { items } = await requete<{ items: GalerieListe[] }>('/v1/console/galleries');
  return items;
}

/**
 * `invitationEnvoyee` dit si le courriel de la direction est réellement PARTI
 * (lot B) : un envoi refusé, ou seulement capturé faute de SMTP, vaut `false`.
 */
export async function creerGalerie(
  payload: NouvelleGalerie,
): Promise<{ id: string; slug: string; invitationEnvoyee: boolean; invitationDifferee?: boolean }> {
  if (modeDemo()) return demoApi.creerGalerie(payload);
  return requete('/v1/console/galleries', { methode: 'POST', corps: payload });
}

export async function obtenirGalerie(id: string): Promise<GalerieFiche> {
  if (modeDemo()) {
    const fiche = demoApi.obtenirGalerie(id);
    if (!fiche) throw new ApiError(404, 'galerie_introuvable', 'Galerie introuvable.');
    return fiche;
  }
  return requete<GalerieFiche>(`/v1/console/galleries/${id}`);
}

export async function modifierGalerie(
  id: string,
  // `etat` ne peut pas valoir `resiliee` : `PATCH` la refuse, la résiliation a
  // sa route dédiée et ses garanties (export, purge à +90 jours, confirmation
  // du slug — docs/dev/api.md § 12.6). Les modules ne passent plus par ici
  // (D21) : ils ont leurs routes, `…/modules/:module` et `…/modules`.
  payload: { palier?: Palier; etat?: EtatGalerieModifiable },
): Promise<void> {
  if (modeDemo()) return demoApi.patchGalerie(id, payload);
  await requete(`/v1/console/galleries/${id}`, { methode: 'PATCH', corps: payload });
}

/**
 * Marque ou démarque un espace de test (lot B). Réservé à la direction de
 * Venio : l'API l'exige (`console:mark-test-gallery`).
 */
export async function marquerEspaceTest(
  id: string,
  estTest: boolean,
): Promise<{ id: string; estTest: boolean }> {
  if (modeDemo()) return demoApi.marquerEspaceTest(id, estTest);
  return requete(`/v1/console/galleries/${id}/test`, { methode: 'PUT', corps: { estTest } });
}

/**
 * Renvoie l'invitation d'une direction qui n'a pas encore accepté (lot B) :
 * nouveau lien, l'ancien révoqué. `compteId` désigne le compte de la ligne ;
 * l'API vérifie qu'il est bien direction en attente de cette galerie.
 */
export async function renvoyerInvitation(
  id: string,
  compteId: string,
): Promise<{ email: string; expireLe: string; invitationEnvoyee: boolean }> {
  if (modeDemo()) return demoApi.renvoyerInvitation(id, compteId);
  return requete(`/v1/console/galleries/${id}/invitation`, {
    methode: 'POST',
    corps: { compteId },
  });
}

/** `409`, `details.raison = direction_deja_active` : la direction a déjà accepté. */
export function estDirectionDejaActive(erreur: unknown): boolean {
  if (!(erreur instanceof ApiError)) return false;
  const details = erreur.details as { raison?: unknown } | null;
  return erreur.status === 409 && details?.raison === 'direction_deja_active';
}

// ── Thème de la régie (lot C) ─────────────────────────────────────────────

/** Pose le thème de la régie et/ou le choix laissé aux utilisateurs de la galerie. */
export async function modifierApparence(
  id: string,
  payload: Partial<Pick<ApparenceGalerie, 'theme' | 'choixThemeClient'>>,
): Promise<ApparenceGalerie> {
  if (modeDemo()) return demoApi.modifierApparence(id, payload);
  return requete<ApparenceGalerie>(`/v1/console/galleries/${id}/apparence`, {
    methode: 'PUT',
    corps: payload,
  });
}

// ── Modules (D21) ─────────────────────────────────────────────────────────

export async function obtenirModules(id: string): Promise<ReponseModules> {
  if (modeDemo()) return demoApi.obtenirModules(id);
  return requete<ReponseModules>(`/v1/console/galleries/${id}/modules`);
}

/** Active ou désactive un module complémentaire. Désactiver ne supprime aucune donnée. */
export async function basculerModule(
  id: string,
  module: string,
  actif: boolean,
): Promise<ReponseModules> {
  if (modeDemo()) return demoApi.basculerModule(id, module, actif);
  return requete<ReponseModules>(
    `/v1/console/galleries/${id}/modules/${encodeURIComponent(module)}`,
    { methode: 'PUT', corps: { actif } },
  );
}

/**
 * Remplace la liste des modules optionnels d'une galerie, sous la révision
 * affichée : si les modules ont bougé depuis, l'API refuse (409) plutôt que
 * d'écraser un changement qu'on n'a pas vu.
 */
export async function remplacerModules(
  id: string,
  modulesActifs: readonly string[],
  revision: number,
): Promise<ReponseModules> {
  if (modeDemo()) return demoApi.remplacerModules(id, modulesActifs);
  return requete<ReponseModules>(`/v1/console/galleries/${id}/modules`, {
    methode: 'PUT',
    corps: { modulesActifs, revision },
  });
}

export async function obtenirInterrupteurs(): Promise<Interrupteurs> {
  if (modeDemo()) return demoApi.obtenirInterrupteurs();
  return requete<Interrupteurs>('/v1/console/interrupteurs');
}

/** Coupe ou rétablit un module pour toutes les galeries. Le motif est obligatoire. */
export async function basculerInterrupteur(
  module: string,
  coupe: boolean,
  motif: string,
): Promise<Interrupteurs> {
  if (modeDemo()) return demoApi.basculerInterrupteur(module, coupe, motif);
  return requete<Interrupteurs>(`/v1/console/interrupteurs/${encodeURIComponent(module)}`, {
    methode: 'PUT',
    corps: { coupe, motif },
  });
}

/** Les paliers de développement et les modules qu'ils débloquent (investisseurs associés). */
export async function obtenirPaliers(): Promise<PaliersVue> {
  if (modeDemo()) return demoApi.obtenirPaliers();
  return requete<PaliersVue>('/v1/console/paliers');
}

/**
 * Débloque ou verrouille tout un palier. `revision` est celle qu'on a lue : si
 * les paliers ont bougé depuis, l'API répond 409 (`revision_paliers_perimee`).
 */
export async function basculerPalier(
  palier: IdPalierDev,
  debloque: boolean,
  motif: string,
  revision: number,
): Promise<PaliersVue> {
  if (modeDemo()) return demoApi.basculerPalier(palier, debloque, motif, revision);
  return requete<PaliersVue>(`/v1/console/paliers/${encodeURIComponent(palier)}`, {
    methode: 'PUT',
    corps: { debloque, motif, revision },
  });
}

/** Débloque ou verrouille un seul module, sous la même révision. */
export async function basculerModulePalier(
  module: string,
  debloque: boolean,
  motif: string,
  revision: number,
): Promise<PaliersVue> {
  if (modeDemo()) return demoApi.basculerModulePalier(module, debloque, motif, revision);
  return requete<PaliersVue>(`/v1/console/paliers/modules/${encodeURIComponent(module)}`, {
    methode: 'PUT',
    corps: { debloque, motif, revision },
  });
}

/** Versions minimale et conseillée des apps iOS et Android, avec la révision à renvoyer. */
export async function obtenirVersionsApps(): Promise<VersionsAppsVue> {
  if (modeDemo()) return demoVersionsApps.obtenir();
  return requete<VersionsAppsVue>('/v1/console/apps/versions');
}

/**
 * Remplace tout le réglage d'une plateforme. `revision` est celle qu'on a
 * lue : si les versions ont bougé depuis, l'API répond 409
 * (`revision_versions_perimee`).
 */
export async function modifierVersionsApp(
  plateforme: PlateformeApp,
  reglage: ExigencesPlateforme,
  revision: number,
): Promise<VersionsAppsVue> {
  if (modeDemo()) return demoVersionsApps.modifier(plateforme, reglage, revision);
  return requete<VersionsAppsVue>(`/v1/console/apps/versions/${encodeURIComponent(plateforme)}`, {
    methode: 'PUT',
    corps: { ...reglage, revision },
  });
}

export async function ajouterDomaine(id: string, hote: string): Promise<Domaine> {
  if (modeDemo()) return demoApi.ajouterDomaine(id, hote);
  return requete(`/v1/console/galleries/${id}/domains`, { methode: 'POST', corps: { hote } });
}

export async function verifierDomaine(id: string, hote: string): Promise<void> {
  if (modeDemo()) return demoApi.verifierDomaine(id, hote);
  await requete(`/v1/console/galleries/${id}/domains/${encodeURIComponent(hote)}/verify`, {
    methode: 'POST',
  });
}

export async function rendreDomainePrincipal(id: string, hote: string): Promise<Domaine> {
  if (modeDemo()) return demoApi.rendreDomainePrincipal(id, hote);
  return requete(`/v1/console/galleries/${id}/domains/${encodeURIComponent(hote)}/principal`, {
    methode: 'POST',
  });
}

export async function retirerDomaine(id: string, hote: string): Promise<void> {
  if (modeDemo()) return demoApi.retirerDomaine(id, hote);
  await requete(`/v1/console/galleries/${id}/domains/${encodeURIComponent(hote)}`, {
    methode: 'DELETE',
  });
}

export async function demanderExport(id: string): Promise<{ jobId: string }> {
  if (modeDemo()) return demoApi.demanderExport(id);
  return requete(`/v1/console/galleries/${id}/export`, { methode: 'POST' });
}

export async function renvoyerExport(idGalerie: string, idExport: string): Promise<RenvoiExport> {
  if (modeDemo()) return demoApi.renvoyerExport(idGalerie, idExport);
  return requete(`/v1/console/galleries/${idGalerie}/exports/${idExport}/envoi`, {
    methode: 'POST',
    corps: {},
  });
}

export async function demanderTestRestauration(id: string): Promise<{ jobId: string }> {
  if (modeDemo()) return demoApi.demanderTestRestauration(id);
  return requete(`/v1/console/galleries/${id}/backup-restore-test`, { methode: 'POST' });
}

export async function demanderAccesSupport(
  id: string,
  motif: string,
  role: RoleAccesSupport,
): Promise<AccesSupport> {
  if (modeDemo()) return demoApi.demanderAccesSupport(id, motif, role);
  return requete(`/v1/console/galleries/${id}/support-access`, {
    methode: 'POST',
    corps: { motif, role },
  });
}

export async function revoquerAccesSupport(id: string): Promise<void> {
  if (modeDemo()) return demoApi.revoquerAccesSupport(id);
  await requete(`/v1/console/galleries/${id}/support-access`, { methode: 'DELETE' });
}

export async function resilierGalerie(id: string, confirmation: string): Promise<void> {
  if (modeDemo()) return demoApi.resilierGalerie(id);
  await requete(`/v1/console/galleries/${id}/terminate`, {
    methode: 'POST',
    corps: { confirmation },
  });
}

/**
 * LANCE la purge d'une galerie résiliée (direction de Venio). Sans motif, elle
 * exige l'accusé de réception de l'export final ; avec un motif écrit, elle
 * s'en passe (forçage journalisé).
 */
export async function lancerPurge(
  id: string,
  confirmation: string,
  motif?: string,
): Promise<{ jobId: string }> {
  if (modeDemo()) return demoApi.lancerPurge(id, motif);
  return requete(`/v1/console/galleries/${id}/purge`, {
    methode: 'POST',
    corps: motif === undefined ? { confirmation } : { confirmation, motif },
  });
}

/**
 * REMET À ZÉRO le compteur d'opérations d'une galerie (direction de Venio,
 * relecture ARTO-54 4, R2) : seulement après une panne qui l'a laissé
 * bloqué. Motif écrit, journalisé ; refusé tant qu'un travail tourne.
 */
export async function remettreOperationsAZero(
  id: string,
  motif: string,
): Promise<{ operationsEnCours: 0; avant: number }> {
  if (modeDemo()) return demoApi.remettreOperationsAZero(id);
  return requete(`/v1/console/galleries/${id}/operations/remise-a-zero`, {
    methode: 'POST',
    corps: { motif },
  });
}

/**
 * REPREND, sous supervision, une purge commencée sous un protocole antérieur
 * dont la barrière est restée posée (direction de Venio, relecture ARTO-54 6,
 * B1). Confirmation par le slug, motif écrit, journalisé.
 */
export async function reprendrePurgeSupervisee(
  id: string,
  confirmation: string,
  motif: string,
): Promise<{ jobId: string }> {
  if (modeDemo()) return demoApi.reprendrePurgeSupervisee(id);
  return requete(`/v1/console/galleries/${id}/purge/reprise-supervisee`, {
    methode: 'POST',
    corps: { confirmation, motif },
  });
}

/**
 * Déclare VÉRIFIÉ l'historique des dépôts de sauvegarde d'une galerie
 * (direction de Venio, B2) : liste exhaustive des dépôts, motif écrit.
 */
export async function verifierHistoriqueDepots(
  id: string,
  depots: readonly string[],
  motif: string,
): Promise<{ historiqueDepotsInconnu: false; depotsSauvegarde: string[] }> {
  if (modeDemo()) return demoApi.verifierHistoriqueDepots(id, depots);
  return requete(`/v1/console/galleries/${id}/depots-sauvegarde/historique-verifie`, {
    methode: 'POST',
    corps: { depots: [...depots], motif },
  });
}

// ── Travaux ───────────────────────────────────────────────────────────────

export async function listerTravaux(filtres: FiltresTravaux = {}): Promise<PageTravaux> {
  const page = filtres.page ?? 1;
  if (modeDemo()) return demoApi.pageTravaux({ ...filtres, page });
  const params = new URLSearchParams();
  if (filtres.galerie) params.set('gallery', filtres.galerie);
  if (filtres.etat) params.set('etat', filtres.etat);
  if (filtres.type) params.set('type', filtres.type);
  params.set('ignores', filtres.masquerIgnores ? 'masquer' : 'inclure');
  params.set('page', String(page));
  const reponse = await requete<Partial<PageTravaux> & { items: Travail[] }>(
    `/v1/console/jobs?${params.toString()}`,
  );
  // Une API plus ancienne ne rend que `items` : une seule page, sans filtre de type.
  return {
    items: reponse.items,
    page: reponse.page ?? page,
    total: reponse.total ?? reponse.items.length,
    types: reponse.types ?? [],
  };
}

export async function relancerTravail(id: string): Promise<void> {
  if (modeDemo()) return demoApi.relancerTravail(id);
  await requete(`/v1/console/jobs/${id}/retry`, { methode: 'POST' });
}

// ── Journal ───────────────────────────────────────────────────────────────

export async function listerJournal(gallery?: string, page = 1): Promise<JournalEntree[]> {
  if (modeDemo()) return demoApi.listerJournal();
  const params = new URLSearchParams();
  if (gallery) params.set('gallery', gallery);
  params.set('page', String(page));
  const { items } = await requete<{ items: JournalEntree[] }>(
    `/v1/console/audit?${params.toString()}`,
  );
  return items;
}

// ── Santé ─────────────────────────────────────────────────────────────────

export async function sante(): Promise<Sante> {
  if (modeDemo()) return demoApi.sante();
  // `/health` (et donc `/v1/console/health`) répond 503 en dégradé, mais avec
  // le même objet que 200 (docs/dev/api.md §10) : jamais l'enveloppe d'erreur
  // `{code, message, details}`. On ne passe donc pas par `requete()`.
  const reponse = await fetch('/v1/console/health', { credentials: 'include' });
  return (await reponse.json()) as Sante;
}

// ── Vue d'aigle (ARTO-58) ─────────────────────────────────────────────────

/**
 * La vue d'aigle (`GET /v1/console/vue-d-aigle`, `CONTRAT-API.md`). C'est
 * l'API qui filtre selon le rôle : l'associé reçoit la vue `associe` quoi
 * qu'il demande ; un rôle opérationnel passe `vue = 'associe'` pour « voir
 * comme un associé ». Le front n'a rien à masquer dans la réponse.
 */
export async function lireVueDAigle(
  periode: PeriodeVueDAigle,
  vue?: NomVueDAigle,
): Promise<VueDAigle> {
  if (modeDemo()) return demoApi.vueDAigle(periode, vue);
  const parametres = new URLSearchParams({ periode });
  if (vue !== undefined) parametres.set('vue', vue);
  return requete<VueDAigle>(`/v1/console/vue-d-aigle?${parametres.toString()}`);
}

// ── Saisies financières (ARTO-58) ─────────────────────────────────────────

/**
 * Les six ressources saisies (`docs/dev/api.md` § 12.2) : tarifs, abonnements,
 * revenus ponctuels, sommes dues, coûts, jalons. Un champ montant vide part en
 * `null` (« à saisir »), jamais en zéro : le corps est construit par l'écran,
 * ce client n'ajoute ni ne retire aucun champ. Pas de suppression d'abonnement
 * côté API : un abonnement se résilie.
 */
export async function lireSaisies<R extends RessourceSaisie>(
  ressource: R,
): Promise<SaisiesParRessource[R][]> {
  if (modeDemo()) return demoSaisies.lire(ressource);
  return requete<SaisiesParRessource[R][]>(`/v1/console/${ressource}`);
}

export async function creerSaisie<R extends RessourceSaisie>(
  ressource: R,
  corps: CorpsSaisie,
): Promise<SaisiesParRessource[R]> {
  if (modeDemo()) return demoSaisies.creer(ressource, corps);
  return requete<SaisiesParRessource[R]>(`/v1/console/${ressource}`, {
    methode: 'POST',
    corps,
  });
}

export async function modifierSaisie<R extends RessourceSaisie>(
  ressource: R,
  id: string,
  corps: CorpsSaisie,
): Promise<SaisiesParRessource[R]> {
  if (modeDemo()) return demoSaisies.modifier(ressource, id, corps);
  return requete<SaisiesParRessource[R]>(`/v1/console/${ressource}/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

export async function supprimerSaisie(ressource: RessourceSaisie, id: string): Promise<void> {
  if (modeDemo()) return demoSaisies.supprimer(ressource, id);
  await requete<void>(`/v1/console/${ressource}/${encodeURIComponent(id)}`, {
    methode: 'DELETE',
  });
}

// ── Associés et partage (ARTO-58, direction) ──────────────────────────────

/**
 * Vérifie le second facteur de la session (`POST /v1/auth/2fa/verify`,
 * `docs/dev/api.md` § 3) : l'élévation que `POST` et `PATCH /associes`
 * exigent, valable quinze minutes.
 */
export async function verifierDeuxFacteurs(code: string, secours = false): Promise<void> {
  if (modeDemo()) return demoSaisies.verifierDeuxFacteurs(code);
  await requete('/v1/auth/2fa/verify', {
    methode: 'POST',
    corps: secours ? { codeSecours: code } : { code },
  });
}

/**
 * `403 acces.refuse`, `details.raison = mfa_enrolement_requis` : la session
 * est confinée à l'enrôlement du second facteur, quelle que soit la route.
 */
export function estErreurConfinement(erreur: unknown): boolean {
  if (!(erreur instanceof ApiError)) return false;
  const details = erreur.details as { raison?: unknown } | null;
  return erreur.status === 403 && details?.raison === 'mfa_enrolement_requis';
}

// ── Second facteur : enrôlement depuis la console (ARTO-58) ───────────────

/** Secret et URI de démonstration : aucun appareil réel ne s'y enrôle. */
const SECRET_DEMO = 'JBSWY3DPEHPK3PXP';
const CODES_SECOURS_DEMO = [
  'a1b2-c3d4',
  'e5f6-a7b8',
  'c9d0-e1f2',
  'a3b4-c5d6',
  'e7f8-a9b0',
  'c1d2-e3f4',
  'a5b6-c7d8',
  'e9f0-a1b2',
  'c3d4-e5f6',
  'a7b8-c9d0',
];

/**
 * Demande un secret d'attente : rien n'est actif tant que `/confirm` n'a
 * pas réussi. L'API ne rend qu'une URI `otpauth://` ; le QR se dessine ici.
 */
export async function enrolerDeuxFacteurs(): Promise<{ secret: string; otpauth: string }> {
  if (modeDemo()) {
    return {
      secret: SECRET_DEMO,
      otpauth: `otpauth://totp/Artosera:demo?secret=${SECRET_DEMO}&issuer=Artosera`,
    };
  }
  return requete('/v1/auth/2fa/enroll', { methode: 'POST' });
}

/** Active le second facteur sur un code valide ; rend les codes de secours, une seule fois. */
export async function confirmerDeuxFacteurs(code: string): Promise<{ codesSecours: string[] }> {
  if (modeDemo()) return { codesSecours: [...CODES_SECOURS_DEMO] };
  return requete('/v1/auth/2fa/confirm', { methode: 'POST', corps: { code } });
}

/** Régénère les codes de secours sur un code TOTP frais (jamais un code de secours). */
export async function regenererCodesSecours(code: string): Promise<{ codesSecours: string[] }> {
  if (modeDemo()) return { codesSecours: [...CODES_SECOURS_DEMO] };
  return requete('/v1/auth/2fa/backup-codes', { methode: 'POST', corps: { code } });
}

/** `403 acces.refuse`, `details.raison = mfa_step_up_requis` : le geste attend une élévation. */
export function estErreurStepUpRequis(erreur: unknown): boolean {
  if (!(erreur instanceof ApiError)) return false;
  const details = erreur.details as { raison?: unknown } | null;
  return erreur.status === 403 && details?.raison === 'mfa_step_up_requis';
}

export async function listerAssocies(): Promise<CompteAssocie[]> {
  if (modeDemo()) return demoSaisies.listerAssocies();
  const { items } = await requete<{ items: CompteAssocie[] }>('/v1/console/associes');
  return items;
}

/** Exige l'élévation du second facteur (`mfa_step_up_requis`). */
export async function inviterAssocie(corps: {
  email: string;
  nom: string;
  langue?: 'fr' | 'en';
}): Promise<InvitationAssocie> {
  if (modeDemo()) return demoSaisies.inviterAssocie(corps);
  return requete<InvitationAssocie>('/v1/console/associes', { methode: 'POST', corps });
}

/** Désactiver, réactiver ou réinitialiser la 2FA. Exige l'élévation du second facteur. */
export async function modifierAssocie(
  id: string,
  corps: { actif: boolean } | { reinitialiserDeuxFacteurs: true },
): Promise<void> {
  if (modeDemo()) return demoSaisies.modifierAssocie(id, corps);
  await requete(`/v1/console/associes/${encodeURIComponent(id)}`, { methode: 'PATCH', corps });
}

/**
 * Annule l'invitation d'associé en attente de ce compte : le lien envoyé ne
 * sert plus. Exige l'élévation du second facteur.
 */
export async function annulerInvitationAssocie(id: string): Promise<void> {
  if (modeDemo()) return demoSaisies.annulerInvitationAssocie(id);
  await requete(`/v1/console/associes/${encodeURIComponent(id)}/invitation`, {
    methode: 'DELETE',
  });
}

export async function listerBeneficiaires(): Promise<BeneficiaireSaisie[]> {
  if (modeDemo()) return demoSaisies.listerBeneficiaires();
  const { items } = await requete<{ items: BeneficiaireSaisie[] }>(
    '/v1/console/partage/beneficiaires',
  );
  return items;
}

export async function creerBeneficiaire(corps: CorpsSaisie): Promise<{ id: string }> {
  if (modeDemo()) return demoSaisies.creerBeneficiaire(corps as Omit<BeneficiaireSaisie, 'id'>);
  return requete('/v1/console/partage/beneficiaires', { methode: 'POST', corps });
}

export async function modifierBeneficiaire(
  id: string,
  corps: CorpsSaisie,
): Promise<{ id: string }> {
  if (modeDemo()) return demoSaisies.modifierBeneficiaire(id, corps);
  return requete(`/v1/console/partage/beneficiaires/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

/**
 * Désigne le bénéficiaire qui reçoit le reste (`recoitLeReste`), en UNE
 * écriture (`PUT /partage/reste`) : le serveur valide le nouveau avant de
 * retirer le reste à l'ancien, tout ou rien. Un refus laisse l'ancien en place.
 */
export async function designerReste(nouveau: string): Promise<void> {
  if (modeDemo()) return demoSaisies.transfererReste(nouveau);
  await requete('/v1/console/partage/reste', {
    methode: 'PUT',
    corps: { beneficiaireId: nouveau },
  });
}

/** Exclut une galerie de la part d'un bénéficiaire, à partir d'un mois. */
export async function ajouterExclusion(
  beneficiaireId: string,
  corps: { galerieId: string; depuisMois: string },
): Promise<void> {
  if (modeDemo()) return demoSaisies.ajouterExclusion(beneficiaireId, corps);
  await requete(
    `/v1/console/comptabilite/beneficiaires/${encodeURIComponent(beneficiaireId)}/exclusions`,
    { methode: 'POST', corps },
  );
}

/** Retire l'exclusion d'une galerie. */
export async function retirerExclusion(beneficiaireId: string, galerieId: string): Promise<void> {
  if (modeDemo()) return demoSaisies.retirerExclusion(beneficiaireId, galerieId);
  await requete<void>(
    `/v1/console/comptabilite/beneficiaires/${encodeURIComponent(
      beneficiaireId,
    )}/exclusions/${encodeURIComponent(galerieId)}`,
    { methode: 'DELETE' },
  );
}

export async function supprimerBeneficiaire(id: string): Promise<void> {
  if (modeDemo()) return demoSaisies.supprimerBeneficiaire(id);
  await requete<void>(`/v1/console/partage/beneficiaires/${encodeURIComponent(id)}`, {
    methode: 'DELETE',
  });
}

/**
 * Les versements, et les mois CLÔTURÉS parmi ceux demandés : leur dû est celui
 * de la clôture, l'API refuse de le changer (`409 mois_cloture`).
 */
export async function listerVersements(mois?: string): Promise<VersementsListe> {
  if (modeDemo()) return demoSaisies.listerVersements(mois);
  const recherche = mois ? `?mois=${encodeURIComponent(mois)}` : '';
  const reponse = await requete<{ items: VersementSaisie[]; moisClotures?: string[] }>(
    `/v1/console/partage/versements${recherche}`,
  );
  return { items: reponse.items, moisClotures: reponse.moisClotures ?? [] };
}

export async function creerVersement(corps: CorpsSaisie): Promise<{ id: string }> {
  if (modeDemo()) return demoSaisies.creerVersement(corps as Omit<VersementSaisie, 'id'>);
  return requete('/v1/console/partage/versements', { methode: 'POST', corps });
}

/** Un versement ne change ni de bénéficiaire ni de mois : seuls dû, versé et date. */
export async function modifierVersement(id: string, corps: CorpsSaisie): Promise<{ id: string }> {
  if (modeDemo()) return demoSaisies.modifierVersement(id, corps);
  return requete(`/v1/console/partage/versements/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

/** Les exploitants et leurs galeries (`console:read`) : le choix d'un forfait d'exploitant. */
export async function listerGroupes(): Promise<GroupeSaisie[]> {
  if (modeDemo()) return demoSaisies.listerGroupes();
  const { items } = await requete<{ items: GroupeSaisie[] }>('/v1/console/groupes');
  return items;
}

// ── Comptabilité (ARTO-58) ────────────────────────────────────────────────

/**
 * La comptabilité (`GET /v1/console/comptabilite`, `CONTRAT-API.md`) : douze
 * mois qui finissent au mois courant. L'API choisit la vue selon le rôle
 * (`vue: 'direction' | 'associe'`) ; la console rend ce qu'elle reçoit.
 */
export async function lireComptabilite(): Promise<Comptabilite> {
  if (modeDemo()) return demoComptabilite.lire();
  return requete<Comptabilite>('/v1/console/comptabilite');
}

/** Les versions de la grille de répartition (direction). */
export async function listerVersionsGrille(): Promise<VersionGrille[]> {
  if (modeDemo()) return demoComptabilite.listerVersions();
  const { items } = await requete<{ items: VersionGrille[] }>('/v1/console/comptabilite/regles');
  return items;
}

/** Une nouvelle version : des parts explicites, qui somment 10 000 points de base. */
export async function creerVersionGrille(corps: {
  service: TypeRecetteId;
  depuisMois: string;
  parts: PartsGrille[];
}): Promise<VersionGrille> {
  if (modeDemo()) return demoComptabilite.creerVersion(corps);
  return requete<VersionGrille>('/v1/console/comptabilite/regles', { methode: 'POST', corps });
}

/** Corrige une version : son mois de départ, ses parts — jamais son service. */
export async function modifierVersionGrille(
  id: string,
  corps: { depuisMois?: string; parts?: PartsGrille[] },
): Promise<VersionGrille> {
  if (modeDemo()) return demoComptabilite.modifierVersion(id, corps);
  return requete<VersionGrille>(`/v1/console/comptabilite/regles/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

/** Clôture un mois (direction). Exige l'élévation du second facteur. */
export async function cloturerMois(mois: string): Promise<MoisComptable> {
  if (modeDemo()) return demoComptabilite.cloturer(mois);
  return requete<MoisComptable>(`/v1/console/comptabilite/clotures/${encodeURIComponent(mois)}`, {
    methode: 'POST',
  });
}

/** Rouvre un mois clôturé (direction). Exige l'élévation du second facteur. */
export async function rouvrirMois(mois: string): Promise<void> {
  if (modeDemo()) return demoComptabilite.rouvrir(mois);
  await requete<void>(`/v1/console/comptabilite/clotures/${encodeURIComponent(mois)}`, {
    methode: 'DELETE',
  });
}

/** Les types de recette : les cinq services, puis ceux que la direction a créés. */
export async function listerTypesRecette(): Promise<TypeRecetteVue[]> {
  if (modeDemo()) return demoComptabilite.listerTypes();
  const { items } = await requete<{ items: TypeRecetteVue[] }>('/v1/console/comptabilite/types');
  return items;
}

/** Crée un type de recette : un libellé (l'anglais facultatif), un objectif facultatif. */
export async function creerTypeRecette(corps: CorpsSaisie): Promise<TypeRecetteVue> {
  if (modeDemo()) return demoComptabilite.creerType(corps);
  return requete<TypeRecetteVue>('/v1/console/comptabilite/types', { methode: 'POST', corps });
}

/** Renomme, archive ou rétablit un type, ou en change l'objectif. */
export async function modifierTypeRecette(id: string, corps: CorpsSaisie): Promise<TypeRecetteVue> {
  if (modeDemo()) return demoComptabilite.modifierType(id, corps);
  return requete<TypeRecetteVue>(`/v1/console/comptabilite/types/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

/** Supprime un type qui n'a jamais servi (sinon `409 type_utilise` : on l'archive). */
export async function supprimerTypeRecette(id: string): Promise<void> {
  if (modeDemo()) return demoComptabilite.supprimerType(id);
  await requete<void>(`/v1/console/comptabilite/types/${encodeURIComponent(id)}`, {
    methode: 'DELETE',
  });
}

/** Les encaissements d'un mois, chacun avec sa répartition. */
export async function listerEncaissements(mois: string): Promise<EncaissementsDuMois> {
  if (modeDemo()) return demoComptabilite.listerEncaissements(mois);
  return requete<EncaissementsDuMois>(
    `/v1/console/comptabilite/encaissements?mois=${encodeURIComponent(mois)}`,
  );
}

/** Enregistre une somme reçue : le serveur en tire HT, TVA, TTC et la répartition. */
export async function creerEncaissement(corps: CorpsSaisie): Promise<Encaissement> {
  if (modeDemo()) return demoComptabilite.creerEncaissement(corps);
  return requete<Encaissement>('/v1/console/comptabilite/encaissements', {
    methode: 'POST',
    corps,
  });
}

/** Corrige un encaissement d'un mois ouvert. */
export async function modifierEncaissement(id: string, corps: CorpsSaisie): Promise<Encaissement> {
  if (modeDemo()) return demoComptabilite.modifierEncaissement(id, corps);
  return requete<Encaissement>(`/v1/console/comptabilite/encaissements/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps,
  });
}

/** Supprime un encaissement d'un mois ouvert. */
export async function supprimerEncaissement(id: string): Promise<void> {
  if (modeDemo()) return demoComptabilite.supprimerEncaissement(id);
  await requete<void>(`/v1/console/comptabilite/encaissements/${encodeURIComponent(id)}`, {
    methode: 'DELETE',
  });
}

// ── Connecteurs (M0) ──────────────────────────────────────────────────────

export async function obtenirConnecteurs(id: string): Promise<ReponseConnecteursConsole> {
  if (modeDemo()) return demoApi.obtenirConnecteurs(id);
  return requete<ReponseConnecteursConsole>(`/v1/console/galleries/${id}/connecteurs`);
}

export async function obtenirSso(id: string): Promise<EtatSsoConsole> {
  if (modeDemo()) return demoApi.obtenirSso(id);
  return requete<EtatSsoConsole>(`/v1/console/galleries/${id}/sso`);
}

// ── Demandes d'ouverture (ARTO-188) ───────────────────────────────────────

export interface FiltresDemandesOuverture {
  etat?: EtatDemandeOuverture;
  source?: SourceDemandeOuverture;
  /** Un identifiant de compte, `moi` ou `personne`. */
  responsable?: string;
  q?: string;
  page?: number;
}

export interface PageDemandesOuverture {
  items: DemandeOuvertureResume[];
  page: number;
  total: number;
  /** Le nombre de demandes de chaque état, filtres ignorés : leur somme est le total de la base. */
  compteurs: Record<EtatDemandeOuverture, number>;
}

/** Ce que `PATCH …/demandes-ouverture/:id` accepte : le responsable, une note, ou les deux. */
export interface ModificationDemandeOuverture {
  revision: number;
  responsableId?: string | null;
  note?: string;
}

export async function listerDemandesOuverture(
  filtres: FiltresDemandesOuverture = {},
): Promise<PageDemandesOuverture> {
  if (modeDemo()) return demoDemandesOuverture.lister(filtres);
  const params = new URLSearchParams();
  if (filtres.etat) params.set('etat', filtres.etat);
  if (filtres.source) params.set('source', filtres.source);
  if (filtres.responsable) params.set('responsable', filtres.responsable);
  if (filtres.q) params.set('q', filtres.q);
  params.set('page', String(filtres.page ?? 1));
  return requete<PageDemandesOuverture>(`/v1/console/demandes-ouverture?${params.toString()}`);
}

export async function obtenirDemandeOuverture(id: string): Promise<DemandeOuvertureVue> {
  if (modeDemo()) return demoDemandesOuverture.obtenir(id);
  return requete<DemandeOuvertureVue>(`/v1/console/demandes-ouverture/${id}`);
}

export async function modifierDemandeOuverture(
  id: string,
  corps: ModificationDemandeOuverture,
): Promise<DemandeOuvertureVue> {
  if (modeDemo()) return demoDemandesOuverture.modifier(id, corps);
  return requete<DemandeOuvertureVue>(`/v1/console/demandes-ouverture/${id}`, {
    methode: 'PATCH',
    corps,
  });
}

export async function qualifierDemandeOuverture(
  id: string,
  revision: number,
): Promise<DemandeOuvertureVue> {
  if (modeDemo()) return demoDemandesOuverture.qualifier(id, revision);
  return requete<DemandeOuvertureVue>(`/v1/console/demandes-ouverture/${id}/qualifier`, {
    methode: 'POST',
    corps: { revision },
  });
}

export async function perdreDemandeOuverture(
  id: string,
  revision: number,
  motif: string,
): Promise<DemandeOuvertureVue> {
  if (modeDemo()) return demoDemandesOuverture.perdre(id, revision, motif);
  return requete<DemandeOuvertureVue>(`/v1/console/demandes-ouverture/${id}/perdre`, {
    methode: 'POST',
    corps: { revision, motif },
  });
}

export interface ReponseCreationDossierDemande {
  demande: DemandeOuvertureVue;
  dossier: { id: string; numero: string };
}

export async function creerDossierDemandeOuverture(
  id: string,
  revision: number,
): Promise<ReponseCreationDossierDemande> {
  if (modeDemo()) return demoDemandesOuverture.creerDossier(id, revision);
  return requete<ReponseCreationDossierDemande>(
    `/v1/console/demandes-ouverture/${id}/creer-dossier`,
    { methode: 'POST', corps: { revision } },
  );
}

// ── Dossiers d'ouverture (ARTO-127) ───────────────────────────────────────

export async function listerOuvertures(
  filtres: { etat?: EtatOuverture; responsable?: string; page?: number } = {},
): Promise<PageOuvertures> {
  if (modeDemo()) return demoOuvertures.lister(filtres);
  const params = new URLSearchParams();
  if (filtres.etat) params.set('etat', filtres.etat);
  if (filtres.responsable) params.set('responsable', filtres.responsable);
  params.set('page', String(filtres.page ?? 1));
  return requete<PageOuvertures>(`/v1/console/ouvertures?${params.toString()}`);
}

export async function obtenirOuverture(id: string): Promise<DossierOuverture> {
  if (modeDemo()) return demoOuvertures.obtenir(id);
  return requete<DossierOuverture>(`/v1/console/ouvertures/${id}`);
}

/** `identite.nomCommercial` est exigé ; tout le reste se complète ensuite. */
export async function creerOuverture(corps: SectionsOuverture): Promise<DossierOuverture> {
  if (modeDemo()) return demoOuvertures.creer(corps);
  return requete<DossierOuverture>('/v1/console/ouvertures', { methode: 'POST', corps });
}

/** `409`, `details.raison = revision_ouverture_perimee` si le dossier a changé. */
export async function modifierOuverture(
  id: string,
  corps: ModificationOuverture,
): Promise<DossierOuverture> {
  if (modeDemo()) return demoOuvertures.modifier(id, corps);
  return requete<DossierOuverture>(`/v1/console/ouvertures/${id}`, { methode: 'PATCH', corps });
}

export async function transitionOuverture(
  id: string,
  corps: { vers: EtatOuverture; revision: number; motif?: string },
): Promise<ReponseTransitionOuverture> {
  if (modeDemo()) return demoOuvertures.transition(id, corps);
  return requete<ReponseTransitionOuverture>(`/v1/console/ouvertures/${id}/transition`, {
    methode: 'POST',
    corps,
  });
}

/**
 * Dépose le contrat signé (PDF). Direction de Venio, second facteur RÉCENT :
 * `403 mfa_step_up_requis` sinon (`estErreurStepUpRequis`).
 */
export async function deposerContratOuverture(
  id: string,
  fichier: File,
): Promise<DossierOuverture> {
  if (modeDemo()) return demoOuvertures.deposerContrat(id, fichier.size);
  const formulaire = new FormData();
  formulaire.append('fichier', fichier);
  const reponse = await fetch(`/v1/console/ouvertures/${id}/contrat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Artosera-CSRF': '1' },
    body: formulaire,
  });
  const donnees = (await reponse.json().catch(() => null)) as
    (DossierOuverture & { code?: string; message?: string; details?: unknown }) | null;
  if (!reponse.ok) {
    const refus = new ApiError(
      reponse.status,
      donnees?.code ?? 'erreur_inconnue',
      donnees?.message ?? 'Une erreur est survenue.',
      donnees?.details ?? null,
    );
    if (estErreurConfinement(refus)) signalerConfinement();
    throw refus;
  }
  return donnees as DossierOuverture;
}

/** Le tableau de contrôle de la recette (ARTO-134). `409 galerie_absente` avant la création. */
export async function obtenirRecetteOuverture(id: string): Promise<RecetteOuverture> {
  if (modeDemo()) return demoOuvertures.recette(id);
  return requete<RecetteOuverture>(`/v1/console/ouvertures/${id}/recette`);
}

/** Coche ou décoche une case de la liste de contrôle ; rend le tableau à jour. */
export async function cocherControleRecette(
  id: string,
  corps: { controle: ControleRecette; coche: boolean; note?: string },
): Promise<RecetteOuverture> {
  if (modeDemo()) return demoOuvertures.cocherControle(id, corps);
  return requete<RecetteOuverture>(`/v1/console/ouvertures/${id}/recette/controles`, {
    methode: 'POST',
    corps,
  });
}

/**
 * La direction de Venio passe outre l'absence de validation de la galerie, avec
 * un motif (8 caractères au moins). Second facteur RÉCENT : `estErreurStepUpRequis`.
 */
export async function poserDerogationRecette(id: string, motif: string): Promise<RecetteOuverture> {
  if (modeDemo()) return demoOuvertures.deroger(id, motif);
  return requete<RecetteOuverture>(`/v1/console/ouvertures/${id}/recette/derogation`, {
    methode: 'POST',
    corps: { motif },
  });
}

/**
 * Bascule le dossier (`bascule_planifiee` → `en_service`), ou la reprend après
 * une panne : les étapes faites sont sautées. Direction de Venio, second
 * facteur RÉCENT. Un échec rend l'erreur de l'étape et laisse le dossier en
 * `bascule_planifiee` ; `GET …/recette` la porte dans `bascule.erreur`.
 */
export async function basculerOuverture(id: string): Promise<ReponseBascule> {
  if (modeDemo()) return demoOuvertures.basculer(id);
  return requete<ReponseBascule>(`/v1/console/ouvertures/${id}/basculer`, { methode: 'POST' });
}

/** Idempotente : rejouée après une panne, elle reprend à la première sous-étape non faite. */
export async function creerGalerieOuverture(id: string): Promise<ReponseCreationGalerieOuverture> {
  if (modeDemo()) return demoOuvertures.creerGalerie(id);
  return requete<ReponseCreationGalerieOuverture>(`/v1/console/ouvertures/${id}/creer-galerie`, {
    methode: 'POST',
  });
}

export async function verifierSlug(slug: string): Promise<DisponibiliteSlug> {
  if (modeDemo()) return demoOuvertures.slugDisponible(slug);
  return requete<DisponibiliteSlug>(
    `/v1/console/galleries/slug-disponible?slug=${encodeURIComponent(slug)}`,
  );
}

// ── Demandes de création de site ──────────────────────────────────────────

export async function listerDemandesSite(
  etat: FiltreDemandesSite = 'ouvertes',
): Promise<DemandeSiteConsole[]> {
  if (modeDemo()) return demoDemandesSite.lister(etat);
  const { demandes } = await requete<{ demandes: DemandeSiteConsole[] }>(
    `/v1/console/demandes-site?etat=${etat}`,
  );
  return demandes;
}

/** `409`, `details.raison = transition_interdite` si la demande a déjà changé d'état. */
export async function changerEtatDemandeSite(
  id: string,
  etat: Exclude<EtatDemandeSite, 'envoyee'>,
): Promise<{ demande: Omit<DemandeSiteConsole, 'galerie' | 'demandeurEmail'> }> {
  if (modeDemo()) return demoDemandesSite.changerEtat(id, etat);
  return requete(`/v1/console/demandes-site/${encodeURIComponent(id)}`, {
    methode: 'PATCH',
    corps: { etat },
  });
}

// ── Diagnostics de l'app iOS ──────────────────────────────────────────────

export async function listerDefautsIos(type: FiltreDiagnosticsIos = 'tous'): Promise<DefautIos[]> {
  if (modeDemo()) return demoDiagnosticsIos.lister(type);
  const { defauts } = await requete<{ defauts: DefautIos[] }>(
    `/v1/console/diagnostics-ios?type=${type}`,
  );
  return defauts;
}

export async function lireDiagnosticIos(empreinte: string): Promise<DiagnosticIos> {
  if (modeDemo()) return demoDiagnosticsIos.lire(empreinte);
  const { diagnostic } = await requete<{ diagnostic: DiagnosticIos }>(
    `/v1/console/diagnostics-ios/${encodeURIComponent(empreinte)}`,
  );
  return diagnostic;
}
